import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sprout, ArrowRight, Check, Instagram, Facebook, Linkedin, Building2, MapPin, Loader2,
} from "lucide-react";
import GaugeScore from "../components/GaugeScore";
import { Logo } from "../components/Logo";

const CALC_STEPS = [
  "Analysing social activity",
  "Comparing competitors",
  "Checking content consistency",
  "Matching NicheCom assets",
  "Finding missed opportunities",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [connected, setConnected] = useState({ Instagram: false, Facebook: false, LinkedIn: false });
  const [calcIndex, setCalcIndex] = useState(0);

  const finish = () => {
    localStorage.setItem("growth_onboarded", "1");
    navigate("/app");
  };

  useEffect(() => {
    if (step !== 6) return;
    setCalcIndex(0);
    const iv = setInterval(() => {
      setCalcIndex((i) => {
        if (i >= CALC_STEPS.length - 1) { clearInterval(iv); setTimeout(() => setStep(7), 700); return i; }
        return i + 1;
      });
    }, 750);
    return () => clearInterval(iv);
  }, [step]);

  const next = () => setStep((s) => s + 1);

  const Shell = ({ children, wide }) => (
    <div className="min-h-screen grid place-items-center px-6 py-12" style={{ background: "#F7F8F6" }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className={`w-full ${wide ? "max-w-2xl" : "max-w-lg"}`}
      >
        <div className="flex items-center justify-center mb-8">
          <Logo height={22} />
        </div>
        {children}
      </motion.div>
    </div>
  );

  const Card = ({ children }) => <div className="card p-8 sm:p-10">{children}</div>;
  const Steps = () => (
    <div className="flex items-center justify-center gap-1.5 mt-8">
      {Array.from({ length: 8 }).map((_, i) => (
        <span key={i} className="h-1 rounded-full transition-all duration-300" style={{ width: i === step ? 24 : 8, background: i <= step ? "#93C020" : "#E7E8E4" }} />
      ))}
    </div>
  );

  return (
    <AnimatePresence mode="wait">
      {step === 0 && (
        <Shell key="s0">
          <Card>
            <p className="label-caps">Step 1</p>
            <h1 className="text-4xl font-extrabold tracking-tighter text-ink mt-3 leading-tight">Welcome to Growth</h1>
            <p className="text-ink-2 mt-3 leading-relaxed">Let's see how your brand is performing — and exactly what to do next.</p>
            <button className="btn-primary mt-8 focus-ring" data-testid="onboarding-start" onClick={next}>Get started <ArrowRight size={16} strokeWidth={2} /></button>
          </Card>
          <Steps />
        </Shell>
      )}

      {step === 1 && (
        <Shell key="s1">
          <Card>
            <p className="label-caps">Step 2 · Organisation</p>
            <h2 className="text-2xl font-bold tracking-tight text-ink mt-3">Your organisation</h2>
            <div className="mt-6 rounded-lg border p-4 flex items-center gap-3" style={{ borderColor: "#E7E8E4" }}>
              <span className="grid place-items-center w-11 h-11 rounded-lg" style={{ background: "#F0F2EE" }}><Building2 size={20} strokeWidth={1.5} color="#678A16" /></span>
              <div><div className="font-semibold text-ink">Gilmours</div><div className="text-sm text-ink-2">3 branches · NicheCom client</div></div>
              <Check className="ml-auto" color="#678A16" size={20} strokeWidth={2} />
            </div>
            <button className="btn-primary mt-8 focus-ring" data-testid="onboarding-next" onClick={next}>Continue <ArrowRight size={16} strokeWidth={2} /></button>
          </Card>
          <Steps />
        </Shell>
      )}

      {step === 2 && (
        <Shell key="s2">
          <Card>
            <p className="label-caps">Step 3 · Branch</p>
            <h2 className="text-2xl font-bold tracking-tight text-ink mt-3">Choose your branch</h2>
            <div className="mt-6 grid gap-2">
              {["Padstow", "Truro", "Newquay", "Wadebridge", "Bodmin"].map((b, i) => (
                <div key={b} className={`flex items-center gap-3 rounded-lg border p-3.5 ${i === 0 ? "" : "opacity-60"}`} style={{ borderColor: i === 0 ? "#93C020" : "#E7E8E4", background: i === 0 ? "rgba(147,192,32,.05)" : "#fff" }}>
                  <MapPin size={17} strokeWidth={1.5} color={i === 0 ? "#678A16" : "#9A9A9A"} />
                  <span className="font-medium text-ink">{b}</span>
                  {i === 0 && <Check className="ml-auto" color="#678A16" size={18} strokeWidth={2} />}
                </div>
              ))}
            </div>
            <button className="btn-primary mt-8 focus-ring" data-testid="onboarding-next" onClick={next}>Continue with Padstow <ArrowRight size={16} strokeWidth={2} /></button>
          </Card>
          <Steps />
        </Shell>
      )}

      {step === 3 && (
        <Shell key="s3">
          <Card>
            <p className="label-caps">Step 4 · Channels</p>
            <h2 className="text-2xl font-bold tracking-tight text-ink mt-3">Connect social channels</h2>
            <p className="text-ink-2 mt-2 text-sm">Demo connections simulate instantly.</p>
            <div className="mt-6 grid gap-2">
              {[{ n: "Instagram", I: Instagram }, { n: "Facebook", I: Facebook }, { n: "LinkedIn", I: Linkedin }].map(({ n, I }) => (
                <button key={n} data-testid={`connect-${n.toLowerCase()}`} onClick={() => setConnected((c) => ({ ...c, [n]: true }))}
                  className="flex items-center gap-3 rounded-lg border p-3.5 text-left transition-all" style={{ borderColor: connected[n] ? "#93C020" : "#E7E8E4", background: connected[n] ? "rgba(147,192,32,.05)" : "#fff" }}>
                  <I size={18} strokeWidth={1.5} />
                  <span className="font-medium text-ink">{n}</span>
                  <span className="ml-auto text-sm font-semibold" style={{ color: connected[n] ? "#678A16" : "#9A9A9A" }}>
                    {connected[n] ? <span className="inline-flex items-center gap-1"><Check size={15} strokeWidth={2} /> Connected</span> : "Connect"}
                  </span>
                </button>
              ))}
            </div>
            <button className="btn-primary mt-8 focus-ring" data-testid="onboarding-next" onClick={next}>Continue <ArrowRight size={16} strokeWidth={2} /></button>
          </Card>
          <Steps />
        </Shell>
      )}

      {step === 4 && (
        <Shell key="s4">
          <Card>
            <p className="label-caps">Step 5 · NicheCom</p>
            <div className="mt-3 rounded-lg p-5" style={{ background: "rgba(147,192,32,.08)" }}>
              <div className="flex items-center gap-2"><Check color="#678A16" size={18} strokeWidth={2} /><span className="font-bold text-ink">We found your NicheCom account.</span></div>
              <div className="flex items-end gap-2 mt-4"><span className="text-5xl font-extrabold tracking-tighter" style={{ color: "#678A16" }}>28</span><span className="text-ink-2 mb-1.5">properties photographed this month</span></div>
            </div>
            <button className="btn-primary mt-8 focus-ring" data-testid="onboarding-next" onClick={next}>Continue <ArrowRight size={16} strokeWidth={2} /></button>
          </Card>
          <Steps />
        </Shell>
      )}

      {step === 5 && (
        <Shell key="s5">
          <Card>
            <p className="label-caps">Step 6 · Competitors</p>
            <h2 className="text-2xl font-bold tracking-tight text-ink mt-3">Nearby competitors</h2>
            <p className="text-ink-2 mt-2 text-sm">We've automatically identified your local market.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Prospect Estate Agents", "Parkers", "Romans", "Davis Tate", "Haslams", "+7 more"].map((c) => (
                <span key={c} className="rounded-full border px-3 py-1.5 text-sm font-medium text-ink" style={{ borderColor: "#E7E8E4", background: "#fff" }}>{c}</span>
              ))}
            </div>
            <button className="btn-primary mt-8 focus-ring" data-testid="onboarding-calc" onClick={() => setStep(6)}>Calculate my Growth Score <ArrowRight size={16} strokeWidth={2} /></button>
          </Card>
          <Steps />
        </Shell>
      )}

      {step === 6 && (
        <Shell key="s6">
          <Card>
            <div className="flex flex-col items-center py-6">
              <Loader2 size={30} className="animate-spin" color="#93C020" />
              <h2 className="text-xl font-bold tracking-tight text-ink mt-5">Calculating your Growth Score…</h2>
              <div className="mt-6 w-full max-w-sm space-y-2.5">
                {CALC_STEPS.map((s, i) => (
                  <div key={s} className="flex items-center gap-3 text-sm transition-all duration-300" style={{ opacity: i <= calcIndex ? 1 : 0.35 }}>
                    <span className="grid place-items-center w-5 h-5 rounded-full" style={{ background: i < calcIndex ? "#93C020" : i === calcIndex ? "rgba(147,192,32,.15)" : "#F0F2EE" }}>
                      {i < calcIndex ? <Check size={12} color="#fff" strokeWidth={3} /> : i === calcIndex ? <Loader2 size={12} className="animate-spin" color="#678A16" /> : null}
                    </span>
                    <span className={i <= calcIndex ? "text-ink font-medium" : "text-ink-3"}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </Shell>
      )}

      {step === 7 && (
        <Shell key="s7" wide>
          <Card>
            <div className="flex flex-col items-center text-center py-2">
              <GaugeScore value={74} size={220} />
              <h2 className="text-3xl font-extrabold tracking-tighter text-ink mt-4">Your Growth Score is 74</h2>
              <p className="text-ink-2 mt-2 max-w-md leading-relaxed">You're already ahead of <span className="font-semibold text-ink">68%</span> of comparable branches. But we found:</p>
              <div className="flex items-baseline gap-2 mt-4"><span className="text-4xl font-extrabold tracking-tighter" style={{ color: "#678A16" }}>11</span><span className="text-ink-2">opportunities to improve</span></div>
              <button className="btn-primary mt-8 focus-ring !px-6 !py-3" data-testid="onboarding-enter" onClick={finish}>Enter Growth <ArrowRight size={16} strokeWidth={2} /></button>
            </div>
          </Card>
        </Shell>
      )}
    </AnimatePresence>
  );
}
